import React from "react";
import type { ReactNode } from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import type { PortalResponse } from "../../api/portal/Portal.typed";
import type {
  TheoryTest,
  TheoryTestSubmission,
  TheoryTestTemplate,
} from "../../api/theoryTests/TheoryTest.typed";
import type { TrainingTemplate } from "../../api/training/Training.typed";
import { PortalModel } from "../../api/portal/PortalModel";
import { PortalAdapter } from "../../api/portal/PortalAdapter";
import { TheoryTestModel } from "../../api/theoryTests/TheoryTestModel";
import { TheoryTestAdapter } from "../../api/theoryTests/TheoryTestAdapter";
import { TrainingModel } from "../../api/training/TrainingModel";
import { TrainingAdapter } from "../../api/training/TrainingAdapter";
import { HttpError } from "../../api/zekay/httpClient";
import { useAuth } from "./auth";

interface PortalProviderProps {
  children: ReactNode;
}

export interface PortalContextType {
  getPortalCustomerInfo: () => Promise<PortalResponse | undefined>;
  updatePortalCustomerInfo: (form: FormData) => Promise<void>;
  getPublicTheoryTestTemplate: (trainingTemplateId: string) => Promise<TheoryTestTemplate | null>;
  submitTheoryTest: (
    trainingTemplateId: string,
    userId: string,
    data: TheoryTestSubmission,
  ) => Promise<TheoryTest>;
  getTrainingTemplateById: (templateId: string) => Promise<TrainingTemplate | null>;
}

export const PortalContext = React.createContext<PortalContextType>(null!);

export function PortalProvider(props: PortalProviderProps) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { token, logout } = useAuth();

  const portalModel = React.useMemo(() => new PortalModel(appConfig), [appConfig]);
  const portalClient = React.useMemo(() => new PortalAdapter(portalModel), [portalModel]);
  const theoryTestModel = React.useMemo(() => new TheoryTestModel(appConfig), [appConfig]);
  const theoryTestClient = React.useMemo(
    () => new TheoryTestAdapter(theoryTestModel),
    [theoryTestModel],
  );
  const trainingModel = React.useMemo(() => new TrainingModel(appConfig), [appConfig]);
  const trainingClient = React.useMemo(() => new TrainingAdapter(trainingModel), [trainingModel]);

  const handleUnauthorized = React.useCallback(
    (err: unknown): boolean => {
      if (!(err instanceof HttpError) || err.status !== 401) return false;
      showAlert("Session expirée ou accès révoqué", "warning");
      logout();
      return true;
    },
    [showAlert, logout],
  );

  const getPortalCustomerInfo = React.useCallback(async (): Promise<PortalResponse | undefined> => {
    try {
      return await portalClient.getPortalCustomerInfo(token!);
    } catch (err) {
      if (handleUnauthorized(err)) return undefined;
      if (err instanceof Error) showAlert(err.message, "danger");
      return undefined;
    }
  }, [portalClient, token, handleUnauthorized, showAlert]);

  const updatePortalCustomerInfo = React.useCallback(
    async (form: FormData): Promise<void> => {
      try {
        await portalClient.updatePortalCustomerInfo(form, token!);
      } catch (err) {
        if (handleUnauthorized(err)) {
          throw err;
        }
        if (err instanceof Error) showAlert(err.message, "danger");
        throw err;
      }
    },
    [portalClient, token, handleUnauthorized, showAlert],
  );

  const getPublicTheoryTestTemplate = React.useCallback(
    async (trainingTemplateId: string): Promise<TheoryTestTemplate | null> => {
      try {
        return await theoryTestClient.getPublicTheoryTestTemplate(trainingTemplateId, token!);
      } catch (err) {
        if (handleUnauthorized(err)) return null;
        if (err instanceof Error) showAlert(err.message, "danger");
        return null;
      }
    },
    [theoryTestClient, token, handleUnauthorized, showAlert],
  );

  const getTrainingTemplateById = React.useCallback(
    async (templateId: string): Promise<TrainingTemplate | null> => {
      try {
        return await trainingClient.getTrainingTemplateById(templateId, token!);
      } catch (err) {
        if (handleUnauthorized(err)) return null;
        if (err instanceof Error) showAlert(err.message, "danger");
        return null;
      }
    },
    [trainingClient, token, handleUnauthorized, showAlert],
  );

  const submitTheoryTest = React.useCallback(
    async (
      trainingTemplateId: string,
      userId: string,
      data: TheoryTestSubmission,
    ): Promise<TheoryTest> => {
      try {
        const result = await theoryTestClient.submitTheoryTest(
          trainingTemplateId,
          userId,
          data,
          token!,
        );
        if (!result) {
          throw new Error("Pas de résultat reçu du serveur");
        }
        return result;
      } catch (error) {
        if (handleUnauthorized(error)) {
          throw new Error("Session expirée", { cause: error });
        }
        throw error;
      }
    },
    [theoryTestClient, token, handleUnauthorized],
  );

  const value = React.useMemo<PortalContextType>(
    () => ({
      getPortalCustomerInfo,
      updatePortalCustomerInfo,
      getPublicTheoryTestTemplate,
      submitTheoryTest,
      getTrainingTemplateById,
    }),
    [
      getPortalCustomerInfo,
      updatePortalCustomerInfo,
      getPublicTheoryTestTemplate,
      submitTheoryTest,
      getTrainingTemplateById,
    ],
  );

  return <PortalContext.Provider value={value}>{props.children}</PortalContext.Provider>;
}

export function usePortal(): PortalContextType {
  const ctx = React.useContext(PortalContext);
  if (!ctx) {
    throw new Error("usePortal must be used within a PortalProvider");
  }
  return ctx;
}
